import { watch } from 'vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { authGuard } from '@/auth/routeGuard'
import { COMMITMENT_ROUTES, type CommitmentKind, type CommitmentStage } from '@/cancelFlow/commitment'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useSubscriptionStore } from '@/store/subscription'
// Views are imported **statically** (eager), so the whole app ships as one JS chunk. This is a
// tiny SPA: the route views are 1–3 kB gz each and the heavy deps (growth-ui) are shared, so
// route-level code-splitting saved only ~3 kB gz on first paint while adding per-navigation
// chunk fetches AND a class of failures — a lazy `import()` can reject *after* the chunk fetches
// (seen in prod on Safari), silently stranding a mid-flow navigation. Eager removes that class
// entirely. See docs/decisions/0009-eager-routes.md and docs/architecture/routing.md.
import SignIn from '@/views/SignIn.vue'
import Status from '@/views/Status.vue'
import CancellationScope from '@/views/cancel/CancellationScope.vue'
import CancellationReasons from '@/views/cancel/CancellationReasons.vue'
import ReturnLikelihood from '@/views/cancel/ReturnLikelihood.vue'
import Confirm from '@/views/cancel/Confirm.vue'
import Done from '@/views/cancel/Done.vue'
import Feedback from '@/views/cancel/Feedback.vue'
import CommitmentChange from '@/views/cancel/commitment/CommitmentChange.vue'
import CommitmentOptions from '@/views/cancel/commitment/CommitmentOptions.vue'
import CommitmentEndNow from '@/views/cancel/commitment/CommitmentEndNow.vue'
import CommitmentEndAfter from '@/views/cancel/commitment/CommitmentEndAfter.vue'
import CommitmentPaymentFailed from '@/views/cancel/commitment/CommitmentPaymentFailed.vue'
import UpfrontRefund from '@/views/cancel/commitment/UpfrontRefund.vue'
import UpdatePaymentMethod from '@/views/payment/UpdatePaymentMethod.vue'
import PaymentSuccess from '@/views/payment/PaymentSuccess.vue'
import PaymentFailed from '@/views/payment/PaymentFailed.vue'
import PortalLayout from '@/portal/components/PortalLayout.vue'
import OrdersSection from '@/views/portal/OrdersSection.vue'
import MessagesSection from '@/views/portal/MessagesSection.vue'
import ProfileSection from '@/views/portal/ProfileSection.vue'
import HelpSection from '@/views/portal/HelpSection.vue'

const routes: RouteRecordRaw[] = [
	{
		// The portal shell (KOVA-796): one PortalLayout stays mounted while its children — the four
		// sections — swap in its outlet, so the header, sidebar and side menu never re-render on a section
		// change. A section's sub-routes (an order, an article) go in here too, with the same
		// `portalSection`, and the nav keeps that section lit. Map: docs/architecture/portal.md.
		path: '/',
		component: PortalLayout,
		// `requiresAuth` sits on the shell record: vue-router merges it into every child's meta, so a section
		// (or a sub-route a feature branch adds) can never be left public by a missing flag. The `/` dispatcher
		// child stays reachable signed out because the guard answers `/` before it reads the meta.
		meta: { requiresAuth: true },
		children: [
			{
				// Dispatcher — the guard sends authenticated users to /status, unauthenticated to /signin.
				// The component (and the shell around it) is never actually rendered; the guard always redirects.
				path: '',
				component: { render: () => null },
			},
			{ path: 'orders', component: OrdersSection, meta: { portalSection: 'orders' } },
			{ path: 'messages', component: MessagesSection, meta: { portalSection: 'messages' } },
			{ path: 'profile', component: ProfileSection, meta: { portalSection: 'profile' } },
			{ path: 'help', component: HelpSection, meta: { portalSection: 'help' } },
		],
	},
	{ path: '/signin', component: SignIn },
	{ path: '/status', component: Status, meta: { requiresAuth: true } },
	// Pre-Rx step: only offered while the prescription is under clinical review, entered from the
	// "Before you go…" modal on /status. Deep-linking it is not blocked — it just pushes on to
	// /cancel/reasons.
	//
	// The survey up to the cancel call (`beforeCancel`) is for a plan nobody has cancelled: /status offers
	// no "Manage subscription" once it is, and Back from the receipt must not reach a second cancel call
	// (a `hard` after "renewals only" under review is refused by billing — PR #23 review, 2026-10-23).
	{ path: '/cancel/scope', component: CancellationScope, meta: { requiresAuth: true, beforeCancel: true } },
	{ path: '/cancel/reasons', component: CancellationReasons, meta: { requiresAuth: true, beforeCancel: true } },
	{ path: '/cancel/likelihood', component: ReturnLikelihood, meta: { requiresAuth: true, beforeCancel: true } },
	{ path: '/cancel/confirm', component: Confirm, meta: { requiresAuth: true, beforeCancel: true } },
	{ path: '/cancel/done', component: Done, meta: { requiresAuth: true } },
	{ path: '/cancel/feedback', component: Feedback, meta: { requiresAuth: true } },
	// Commitment cancel flow (DEV-35727). Only a commitment plan in the listed stages (and, where set,
	// of the listed kinds) may open these — the guard below sends everyone else to /status, so the
	// classic chain above stays the only flow a non-commitment can reach. Map:
	// docs/architecture/routing.md → "Commitment cancel flow".
	{
		path: COMMITMENT_ROUTES.change,
		component: CommitmentChange,
		meta: { requiresAuth: true, commitmentStages: ['mid_term', 'last_month'] },
	},
	{
		path: COMMITMENT_ROUTES.options,
		component: CommitmentOptions,
		meta: { requiresAuth: true, commitmentStages: ['mid_term'] },
	},
	// The fee exits — monthly commitments only. Paying today (and its failure screen) stays open after
	// a cancel that leaves the buy-out on offer: "Pay $X and cancel today" on /status (`afterCancel`).
	{
		path: COMMITMENT_ROUTES.endNow,
		component: CommitmentEndNow,
		meta: { requiresAuth: true, commitmentStages: ['mid_term'], commitmentKinds: ['monthly'], afterCancel: true },
	},
	{
		path: COMMITMENT_ROUTES.endAfter,
		component: CommitmentEndAfter,
		meta: { requiresAuth: true, commitmentStages: ['mid_term'], commitmentKinds: ['monthly'] },
	},
	{
		path: COMMITMENT_ROUTES.failed,
		component: CommitmentPaymentFailed,
		meta: { requiresAuth: true, commitmentStages: ['mid_term'], commitmentKinds: ['monthly'], afterCancel: true },
	},
	// The refund exit — paid in full only.
	{
		path: COMMITMENT_ROUTES.refund,
		component: UpfrontRefund,
		meta: { requiresAuth: true, commitmentStages: ['mid_term'], commitmentKinds: ['upfront'] },
	},
	// Self-serve payment-method change (DEV-35117). `/payment/update` doubles as the
	// post-login deep-link target for "change your payment method" emails/CRM links —
	// the auth guard already carries unauthenticated arrivals through /signin?next=…
	// The "Verifying" screen is NOT a route: it overlays the paywall while the change
	// call / 3DS is in flight (the PaymentClient must stay mounted).
	{ path: '/payment/update', component: UpdatePaymentMethod, meta: { requiresAuth: true } },
	{ path: '/payment/success', component: PaymentSuccess, meta: { requiresAuth: true } },
	{ path: '/payment/failed', component: PaymentFailed, meta: { requiresAuth: true } },
	{
		// Catch-all: bounce to /. The dispatcher route then sends them to /status or /signin.
		path: '/:catchAll(.*)',
		redirect: '/',
	},
]

export const router = createRouter({
	history: createWebHistory(),
	routes,
	// Always land at the top of a fresh route. `savedPosition` is non-null only
	// on browser back/forward (popstate), and there we honour what the browser
	// remembered so a Back button feels native. For everything else (`router.push`,
	// link clicks) the user gets a fresh-page-style top scroll — matches the
	// Figma flows, where every cancel-survey screen opens from its title.
	scrollBehavior(_to, _from, savedPosition) {
		if (savedPosition) return savedPosition
		return { top: 0, left: 0 }
	},
})

router.beforeEach(authGuard)

/**
 * Commitment-only routes (`meta.commitmentStages`): the subscription is fetched before mount, so the
 * stage is settled on the first navigation. A non-commitment, or a commitment in another stage (a
 * stale deep link, Back after the plan changed), lands on /status.
 *
 * A plan cancelled in this flow keeps its stage, so that alone would let Back from the receipt reopen
 * the exit screens. An ended plan (the fee paid) opens none of them; one still running out after
 * "Keep my plan" or the instalments opens only the `afterCancel` ones — paying the rest today.
 *
 * The survey before the cancel call (`meta.beforeCancel`, every plan) takes the same cancelled check.
 */
router.beforeEach(async (to) => {
	const stages = to.meta.commitmentStages as CommitmentStage[] | undefined
	if (!stages && !to.meta.beforeCancel) return true
	// The first navigation starts at `app.use(router)`, before main.ts has fetched the subscription
	// (a deep link straight into the flow). Unauthenticated visitors never get here — `authGuard`
	// has already redirected them — so the fetch is guaranteed to settle.
	const subs = useSubscriptionStore()
	if (!subs.bootstrapped) {
		await new Promise<void>((resolve) => {
			const stop = watch(
				() => subs.bootstrapped,
				(ready) => {
					if (!ready) return
					stop()
					resolve()
				},
			)
		})
	}
	const sub = subs.subscription
	if (sub?.status === 'cancelled' || sub?.status === 'declined') return '/status'
	if (sub?.cancelled && !to.meta.afterCancel) return '/status'
	if (!stages) return true
	const flow = useCancelFlowStore()
	const kinds = to.meta.commitmentKinds as CommitmentKind[] | undefined
	if (kinds && !(flow.kind && kinds.includes(flow.kind))) return '/status'
	const stage = flow.stage
	return stage && stages.includes(stage) ? true : '/status'
})
